import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { putWithRetry } from '@/lib/blobUpload';
import type { components } from '@/api/generated/schema';

export type JobPhoto = components['schemas']['JobPhotoResponse'];

const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function photosQueryKey(jobId: string) {
  return ['jobs', jobId, 'photos'] as const;
}

/** A job's photo thread — thumbnail and read URLs are freshly minted SAS links, good for a few minutes only. */
export function useJobPhotos(jobId: string | undefined) {
  return useQuery({
    queryKey: photosQueryKey(jobId ?? ''),
    enabled: jobId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs/{jobId}/photos', {
        params: { path: { jobId: jobId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs/{jobId}/photos failed: ${response.status}`);
      }
      return data;
    },
    // Read SAS URLs expire; a photo section left open across a coffee break
    // should not be showing images with dead links.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export type UploadStatus = 'uploading' | 'processing' | 'done' | 'failed';

export interface UploadItem {
  localId: string;
  fileName: string;
  status: UploadStatus;
}

interface PendingUpload extends UploadItem {
  file: File;
}

/**
 * Drives one photo through the whole flow (M1-14): request a SAS, PUT the
 * file straight to Blob Storage, confirm. Retries the PUT itself — the step
 * most likely to hit a flaky connection — with backoff; a failure anywhere
 * in the flow lands the item in `failed` with the file still held, so
 * `retry` can restart the exact same upload without asking the user to
 * re-pick the file. That is this story's answer to "no data loss on
 * submit" — the richer persistent-across-app-kill queue is M3-07's mobile
 * story, not this one.
 */
export function useUploadJobPhotos(jobId: string | undefined) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<Record<string, PendingUpload>>({});

  const setStatus = useCallback((localId: string, status: UploadStatus) => {
    setUploads((current) =>
      current[localId] ? { ...current, [localId]: { ...current[localId], status } } : current,
    );
  }, []);

  const runUpload = useCallback(
    async (item: PendingUpload) => {
      if (!jobId) {
        return;
      }
      setStatus(item.localId, 'uploading');
      try {
        const { data, response } = await apiClient.POST('/api/v1/jobs/{jobId}/photos', {
          params: { path: { jobId } },
          body: { contentType: item.file.type },
        });
        if (!response.ok || !data) {
          throw new Error(`request-upload failed: ${response.status}`);
        }

        await putWithRetry(data.uploadUrl, item.file);

        setStatus(item.localId, 'processing');
        const complete = await apiClient.POST('/api/v1/jobs/{jobId}/photos/{photoId}/complete', {
          params: { path: { jobId, photoId: data.photoId } },
        });
        if (!complete.response.ok) {
          throw new Error(`complete failed: ${complete.response.status}`);
        }

        setStatus(item.localId, 'done');
        void queryClient.invalidateQueries({ queryKey: photosQueryKey(jobId) });
      } catch {
        setStatus(item.localId, 'failed');
      }
    },
    [jobId, queryClient, setStatus],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const accepted = [...files].filter((file) => ALLOWED_CONTENT_TYPES.includes(file.type));
      const items: PendingUpload[] = accepted.map((file) => ({
        localId: crypto.randomUUID(),
        fileName: file.name,
        status: 'uploading',
        file,
      }));
      if (items.length === 0) {
        return;
      }
      setUploads((current) => {
        const next = { ...current };
        for (const item of items) {
          next[item.localId] = item;
        }
        return next;
      });
      for (const item of items) {
        void runUpload(item);
      }
    },
    [runUpload],
  );

  const retry = useCallback(
    (localId: string) => {
      const item = uploads[localId];
      if (item) {
        void runUpload(item);
      }
    },
    [uploads, runUpload],
  );

  const dismiss = useCallback((localId: string) => {
    setUploads((current) => {
      if (!(localId in current)) {
        return current;
      }
      const next = { ...current };
      delete next[localId];
      return next;
    });
  }, []);

  const items: UploadItem[] = Object.values(uploads).map(({ localId, fileName, status }) => ({
    localId,
    fileName,
    status,
  }));

  return { uploads: items, addFiles, retry, dismiss, acceptedTypes: ALLOWED_CONTENT_TYPES };
}
